import { useEffect, useState } from "react";
import { Loader2, Save, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, Panel, useAdmin } from "@/components/admin/ui";

const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 text-sm outline-none focus:border-[#1F2A22]";

const MODES = [
  ["printify_live", "Live Printify rates", "Charge the customer the exact live shipping rate from Printify."],
  ["flat", "Flat rate", "Charge one flat shipping amount on every order."],
  ["free_over", "Free over a threshold", "Free shipping once the subtotal reaches your threshold — otherwise a flat rate."],
  ["free_always", "Always free", "Never charge the customer for shipping (you absorb the Printify cost)."],
];

export default function AdminCommerceShipping() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("marketplace");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.admShippingSettings().then((d) => setS({
      mode: d.mode || "printify_live",
      flat_dollars: (d.flat_cents || 0) / 100,
      free_over_dollars: (d.free_over_cents || 0) / 100,
    })).catch(() => setS({ mode: "printify_live", flat_dollars: 0, free_over_dollars: 0 }));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const updated = await api.admShippingSettingsUpdate({
        mode: s.mode,
        flat_cents: Math.round(Number(s.flat_dollars || 0) * 100),
        free_over_cents: Math.round(Number(s.free_over_dollars || 0) * 100),
      });
      setS({ mode: updated.mode, flat_dollars: (updated.flat_cents || 0) / 100, free_over_dollars: (updated.free_over_cents || 0) / 100 });
      toast.success("Shipping rules saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  if (!s) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>;
  const showFlat = s.mode === "flat" || s.mode === "free_over";

  return (
    <div className="p-6 md:p-8 max-w-2xl" data-testid="admin-commerce-shipping">
      <div className="flex items-end justify-between gap-4">
        <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Shipping rules</h1></div>
        {canEdit && <button onClick={save} disabled={busy} className={btnPrimary} data-testid="shipping-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>}
      </div>
      <p className="text-sm text-[#5C6B61] mt-2">Choose what customers pay for shipping. The live Printify cost is always calculated behind the scenes so you can see any shipping you absorb (shown on each order).</p>

      <Panel title={<span className="flex items-center gap-2"><Truck size={16} /> Customer shipping charge</span>} className="mt-5">
        <div className="space-y-3">
          {MODES.map(([val, label, hint]) => (
            <label key={val} className={`flex gap-3 p-3 border cursor-pointer ${s.mode === val ? "border-[#FF6A00] bg-[#FF6A00]/5" : "border-[#E5E2DC] bg-white"}`} data-testid={`shipping-mode-${val}`}>
              <input type="radio" name="ship-mode" checked={s.mode === val} onChange={() => setS({ ...s, mode: val })} disabled={!canEdit} className="mt-0.5" />
              <span><span className="block text-sm font-medium text-[#1F2A22]">{label}</span><span className="block text-xs text-[#5C6B61] mt-0.5">{hint}</span></span>
            </label>
          ))}
        </div>

        {(showFlat || s.mode === "free_over") && (
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {showFlat && (
              <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Flat rate ($)</span>
                <input type="number" min="0" step="0.01" value={s.flat_dollars} onChange={(e) => setS({ ...s, flat_dollars: e.target.value })} disabled={!canEdit} className={inp + " mt-1"} data-testid="shipping-flat-input" /></label>
            )}
            {s.mode === "free_over" && (
              <label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Free when subtotal ≥ ($)</span>
                <input type="number" min="0" step="0.01" value={s.free_over_dollars} onChange={(e) => setS({ ...s, free_over_dollars: e.target.value })} disabled={!canEdit} className={inp + " mt-1"} data-testid="shipping-freeover-input" /></label>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
